import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListingStage } from '@/api/v4/project-listings.api';

import { getSpotlightStageLabel } from '../stage-pills/get-spotlight-stage-label';
import type { StagePillKey } from '../stage-pills/spotlight-company-stage-pills';
import {
  ClearFiltersButton,
  StageFiltersContainer,
  StageFiltersDivider,
  StageFiltersFooter,
  StageFiltersRow,
  StageFiltersTitle,
  StageFilterTagButton,
} from './spotlight-companies-listing.styles';

const STAGE_FILTER_OPTIONS: StagePillKey[] = [
  'investment',
  'deal',
  'pilot',
  'editorsChoice',
];

interface SpotlightStageFiltersPanelProps {
  hasActiveStageFilters: boolean;
  isOpen: boolean;
  listingStages: ListingStage[];
  onClearStageFilters: () => void;
  onClose: () => void;
  onToggleStageFilter: (stageFilter: StagePillKey) => void;
  selectedStageFilters: StagePillKey[];
}

export const SpotlightStageFiltersPanel = ({
  hasActiveStageFilters,
  isOpen,
  listingStages,
  onClearStageFilters,
  onClose,
  onToggleStageFilter,
  selectedStageFilters,
}: SpotlightStageFiltersPanelProps) => {
  const { t } = useTranslation('spotlight');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) {
        return;
      }

      const isClickInsideFiltersContainer = Boolean(
        eventTarget.closest('[data-stage-filters-root="true"]'),
      );
      const isClickInsideFilterButton = Boolean(
        eventTarget.closest('[data-stage-filters-toggle="true"]'),
      );

      if (!isClickInsideFiltersContainer && !isClickInsideFilterButton) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <StageFiltersContainer data-stage-filters-root="true">
      <StageFiltersTitle>{t('stageFiltersTitle')}</StageFiltersTitle>
      <StageFiltersRow>
        {STAGE_FILTER_OPTIONS.map(stageFilter => (
          <StageFilterTagButton
            key={stageFilter}
            type="button"
            onClick={() => onToggleStageFilter(stageFilter)}
            $isSelected={selectedStageFilters.includes(stageFilter)}
          >
            {getSpotlightStageLabel(
              stageFilter,
              listingStages,
              t('stage.editorsChoice'),
            )}
          </StageFilterTagButton>
        ))}
      </StageFiltersRow>
      {hasActiveStageFilters && (
        <StageFiltersFooter>
          <StageFiltersDivider />
          <ClearFiltersButton type="button" onClick={onClearStageFilters}>
            {t('clearAllFiltersCount', {
              count: selectedStageFilters.length,
            })}
          </ClearFiltersButton>
        </StageFiltersFooter>
      )}
    </StageFiltersContainer>
  );
};
